import { useEffect, useState, useRef } from "react";
import { useFeatures } from "@/context/FeaturesContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Briefcase, Users, Store, Send } from "lucide-react";
import { api } from "@/lib/api";
import { Section, Stat, Chip, CTALink } from "@/components/ui-brand";

const AIHero = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [val, setVal] = useState("");
  const prompts = [
    "Contame tu formación y en qué te querés desarrollar…",
    "¿Qué habilidades técnicas manejás?",
    "Genial. Redacto tu perfil profesional y tu CV en PDF.",
  ];
  const next = () => {
    if (step < 2) { setStep(step + 1); setVal(""); }
    else navigate("/registro?tipo=egresado");
  };
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
      className="card-brand p-6 sm:p-7 shadow-xl" data-testid="ai-hero-card">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ background: "var(--brand-accent)" }}><Sparkles size={16} /></span>
        <span className="font-heading font-bold text-[var(--brand-primary)]">Asistente de Perfil IA</span>
        <span className="ml-auto flex items-center gap-1.5 text-xs text-[var(--brand-text-muted)]"><span className="w-2 h-2 rounded-full pulse-dot" style={{ background: "var(--status-contactado)" }} /> en línea</span>
      </div>
      <div className="bg-[var(--brand-surface)] rounded-2xl p-4 mb-4 min-h-[72px] text-sm text-[var(--brand-primary)]">
        <motion.p key={step} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{prompts[step]}</motion.p>
      </div>
      <div className="flex gap-2">
        <input value={val} onChange={(e) => setVal(e.target.value)} placeholder="Escribí acá…" data-testid="ai-hero-input"
          onKeyDown={(e) => e.key === "Enter" && next()}
          className="flex-1 min-w-0 rounded-full border-2 border-[var(--brand-border)] px-4 py-2.5 text-sm outline-none focus:border-[var(--brand-accent)]" />
        <button onClick={next} className="btn-accent flex items-center gap-1.5 shrink-0" data-testid="ai-hero-send">
          {step < 2 ? <Send size={16} /> : <><ArrowRight size={16} /> Crear perfil</>}
        </button>
      </div>
      <p className="text-xs text-[var(--brand-text-muted)] mt-3">La IA solo ordena y redacta los datos que vos cargás. Nunca inventa información.</p>
    </motion.div>
  );
};

const TRACCION = [
  { k: "egresados", label: "Egresados/as activos/as", testid: "stat-egresados" },
  { k: "empresas", label: "Empresas adheridas", testid: "stat-empresas" },
  { k: "busquedas_abiertas", label: "Búsquedas abiertas", testid: "stat-busquedas" },
];

const Traccion = ({ stats }) => {
  const visibles = TRACCION.filter((t) => stats[t.k] > 0);
  if (!visibles.length) return (
    <Section className="text-center">
      <div data-testid="stats-empty">
        <p className="font-heading font-bold text-[var(--brand-primary)] text-lg">La comunidad está arrancando</p>
        <p className="text-sm text-[var(--brand-text-muted)] mt-1">Sumate entre las primeras personas y empresas en formar parte.</p>
      </div>
    </Section>
  );
  return (
    <Section className={`grid gap-4 ${visibles.length === 1 ? "grid-cols-1" : visibles.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
      {visibles.map((t) => <Stat key={t.k} value={`+${stats[t.k]}`} label={t.label} testid={t.testid} />)}
    </Section>
  );
};

export default function Home() {
  const [stats, setStats] = useState({ egresados: 0, empresas: 0, busquedas_abiertas: 0 });
  const [feed, setFeed] = useState([]);
  const { comunidad } = useFeatures();

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (comunidad) {
      api.get("/public/feed?limit=8").then((r) => setFeed(r.data.items)).catch((e) => console.error("No se pudo cargar el feed", e));
    }
  }, [comunidad]);

  const roles = [
    { icon: Users, title: "Egresados/as", desc: "Armá tu perfil con IA, mostrá tus fortalezas y postulate a búsquedas reales.", cta: "/registro?tipo=egresado", label: "Soy egresado/a" },
    { icon: Briefcase, title: "Empresas", desc: "Publicá búsquedas y encontrá talento formado por Córdoba Crea Futuro, con ayuda de un asistente de IA.", cta: "/empresas", label: "Soy empresa" },
    { icon: Store, title: "Emprendedores/as", desc: "Sumá tu producto o servicio a la vidriera pública de la comunidad.", cta: "/vidriera", label: "Ver vidriera" },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="hero-grid-bg border-b border-slate-100">
        <Section className="py-14 sm:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="fade-up min-w-0">
            <Chip accent testid="hero-eyebrow"><Sparkles size={13} /> Bolsa de talentos Córdoba Crea Futuro</Chip>
            <h1 className="h-hero text-3xl sm:text-5xl lg:text-6xl mt-5">
              Tu formación, el impulso para<br /><span style={{ color: "var(--brand-accent)" }}>tu próximo empleo</span>
            </h1>
            <p className="text-base sm:text-lg text-[var(--brand-text-muted)] mt-5 max-w-lg leading-relaxed">
              Mostrá lo que aprendiste y tus fortalezas, encontrá búsquedas laborales y sumate a una comunidad donde egresados/as, emprendedores/as y empresas de Córdoba se conectan.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <CTALink to="/registro?tipo=egresado" testid="hero-egresado-cta">Empezar como egresado/a</CTALink>
            </div>
          </div>
          <AIHero />
        </Section>
      </section>

      {/* TRACTION: only non-zero counters; friendly message while the community is starting */}
      <section className="py-12 bg-[var(--brand-surface)] border-b border-slate-100">
        <Traccion stats={stats} />
      </section>

      {/* ROLES */}
      <Section className="py-16">
        <h2 className="h-section text-2xl sm:text-3xl text-center">Una plataforma, tres protagonistas</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {roles.map((r, i) => (
            <motion.div key={r.title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="card-brand p-7" data-testid={`role-card-${r.title}`}>
              <span className="w-12 h-12 rounded-2xl flex items-center justify-center text-white mb-4" style={{ background: "var(--brand-primary)" }}><r.icon size={22} /></span>
              <h3 className="font-heading font-bold text-xl text-[var(--brand-primary)]">{r.title}</h3>
              <p className="text-sm text-[var(--brand-text-muted)] mt-2 leading-relaxed">{r.desc}</p>
              <CTALink to={r.cta} variant="outline" testid={`role-cta-${r.title}`}>{r.label}</CTALink>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* COMMUNITY FEED PREVIEW (behind COMUNIDAD_ENABLED) */}
      {comunidad && (
      <section className="py-16 bg-[var(--brand-surface)]" data-testid="home-comunidad">
        <Section>
          <div className="flex items-end justify-between mb-8">
            <div>
              <Chip accent>Comunidad viva</Chip>
              <h2 className="h-section text-2xl sm:text-3xl mt-3">Lo último en la red</h2>
            </div>
            <CTALink to="/comunidad" variant="outline" testid="feed-vermas">Ver todo</CTALink>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {feed.slice(0, 8).map((f) => (
              <div key={f.id} className="card-brand p-5" data-testid={`feed-item-${f.id}`}>
                <Chip accent={f.tipo === "busqueda"}>{f.tipo === "busqueda" ? "Nueva búsqueda" : "Emprendimiento"}</Chip>
                <p className="font-heading font-bold text-[var(--brand-primary)] mt-3 leading-snug">{f.titulo}</p>
                <p className="text-xs text-[var(--brand-text-muted)] mt-1">{f.detalle}</p>
              </div>
            ))}
          </div>
        </Section>
      </section>
      )}
    </div>
  );
}
